import { useCallback, useMemo, useState } from 'react';

import { EMPTY_STRING } from '@e-socna/empty';
import type { MissionCategoryId } from '@e-socna/data-layer';

import { IdAutocomplete } from '../IdAutocomplete';
import type { IdOption } from '../IdAutocomplete';

import type { ReferencesContext_MissionCategory } from '../../contexts/ReferencesContext';

interface Props {
  readonly disabled?: boolean;
  readonly error?: boolean;
  readonly helperText?: string | null;
  readonly initiallyOpen?: boolean;
  readonly label?: string | null;
  readonly name?: string | null;
  readonly onBlur?: (() => void) | null;
  readonly onChange: (id: MissionCategoryId | null) => void;
  readonly options: readonly ReferencesContext_MissionCategory[];
  readonly value: MissionCategoryId | null;
}

export const LevelSelect = ({
  disabled = false,
  error = false,
  helperText = null,
  initiallyOpen = false,
  label = null,
  name = null,
  onBlur = null,
  onChange,
  options,
  value,
}: Readonly<Props>) => {
  const [open, setOpen] = useState(initiallyOpen);

  const idOptions = useMemo<readonly IdOption<MissionCategoryId>[]>(
    () => options.map((option) => ({ id: option._id, label: option.label })),
    [options],
  );

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleChange = useCallback(
    (id: MissionCategoryId | typeof EMPTY_STRING) => {
      onChange(id || null);
    },
    [onChange],
  );

  return (
    <IdAutocomplete<MissionCategoryId, IdOption<MissionCategoryId>>
      disabled={disabled}
      error={error}
      helperText={helperText}
      label={label}
      name={name}
      onBlur={onBlur}
      onChange={handleChange}
      onClose={handleClose}
      onOpen={handleOpen}
      open={open}
      options={idOptions}
      value={value ?? EMPTY_STRING}
    />
  );
};
